export const ASSET_LAYER_VALUES = [
  'G',
  'S',
  'L',
  'M',
  'W',
  'B',
  'P',
  'T',
  'C',
  'R',
] as const;

export type AssetLayer = (typeof ASSET_LAYER_VALUES)[number];

export const ASSET_TYPE_FILTER_VALUES = ['BASE', 'VARIANT'] as const;
export type AssetTypeFilter = (typeof ASSET_TYPE_FILTER_VALUES)[number];

export const GENDER_VALUES = ['MALE', 'FEMALE', 'UNISEX'] as const;
export type Gender = (typeof GENDER_VALUES)[number];

export const GENRE_VALUES = [
  'POP',
  'HIP_HOP',
  'ROCK',
  'ELECTRONIC',
  'RNB',
  'COUNTRY',
  'ALTERNATIVE',
  'JAZZ',
  'CLASSICAL',
  'FOLK',
  'REGGAE',
  'BLUES',
  'INDIE',
  'PUNK',
  'BOLLYWOOD',
  'DANCE',
  'FUNK',
  'GOSPEL',
  'METAL',
  'SOUL',
  'WORLD',
  'OTHER',
] as const;
export type Genre = (typeof GENRE_VALUES)[number];

export const MOOD_VALUES = [
  'ANGRY',
  'ANXIOUS',
  'CALM',
  'CONFIDENT',
  'EMBARRASSED',
  'EXCITED',
  'FEEL_GOOD',
  'HAPPY',
  'INSPIRED',
  'NOSTALGIC',
  'PLAYFUL',
  'ROMANTIC',
  'SAD',
  'SURPRISED',
  'TIRED',
] as const;
export type Mood = (typeof MOOD_VALUES)[number];

export const INTENSITY_LEVEL_VALUES = ['LOW', 'MEDIUM', 'HIGH'] as const;
export type IntensityLevel = (typeof INTENSITY_LEVEL_VALUES)[number];

export const AGE_GROUP_VALUES = [
  'TEEN',
  'YOUNG_ADULT',
  'ADULT',
  'SENIOR',
] as const;
export type AgeGroup = (typeof AGE_GROUP_VALUES)[number];

export const PERFORMANCE_CONTEXT_VALUES = [
  'CONCERT',
  'FESTIVAL',
  'CLUB',
  'PARTY',
  'FORMAL_EVENT',
  'SOCIAL_GATHERING',
  'ROMANTIC_INTIMATE',
  'CASUAL',
  'WORKOUT',
  'STUDY_WORK',
  'RELAXATION',
  'HOME',
] as const;
export type PerformanceContext = (typeof PERFORMANCE_CONTEXT_VALUES)[number];

export const CULTURAL_CONTEXT_VALUES = [
  'NORTH_AMERICAN',
  'LATIN_AMERICAN',
  'EUROPEAN',
  'AFRICAN',
  'MIDDLE_EASTERN',
  'SOUTH_ASIAN',
  'EAST_ASIAN',
  'SOUTHEAST_ASIAN',
  'CARIBBEAN',
  'GLOBAL',
] as const;
export type CulturalContext = (typeof CULTURAL_CONTEXT_VALUES)[number];

export const COLOR_PALETTE_VALUES = [
  'NEUTRAL',
  'BOLD',
  'EARTH_TONES',
  'PASTEL',
  'MONOCHROME',
  'COLORFUL',
  'DARK',
  'BRIGHT',
] as const;
export type ColorPalette = (typeof COLOR_PALETTE_VALUES)[number];

export const SEASONALITY_VALUES = [
  'SUMMER',
  'SPRING',
  'FALL',
  'WINTER',
  'ALL_SEASON',
] as const;
export type Seasonality = (typeof SEASONALITY_VALUES)[number];

export interface AssetDetailsResponse {
  id: string;
  layer: AssetLayer;
  humanFriendlyName?: string;
  machineFriendlyAddress?: string;
  description?: string;
  filePath?: string;
  fileUrl?: string;
  categoryCode?: string;
  categoryName?: string;
  subcategoryCode?: string;
  subcategoryName?: string;
  baseAssetId?: string;
}

export interface TagsResponse {
  tags: string[];
}

export interface StoredAssetDTO {
  id: string;
  humanFriendlyName?: string;
  machineFriendlyAddress?: string;
}

export interface CompositeMetadataResponse {
  songId?: string;
  starId?: string;
  lookId?: string;
  moveId?: string;
  worldId?: string;
}

export interface AlgorhythmMetadataRequest {
  targetAudience: AgeGroup[];
  ageAppropriateness: string[];
  culturalContext: CulturalContext[];
  musicalStyle: string[];
  performanceContext: PerformanceContext[];
}

export interface AlgorhythmMetadataResponse {
  targetAudience?: AgeGroup[];
  ageAppropriateness?: string[];
  culturalContext?: CulturalContext[];
  musicalStyle?: string[];
  performanceContext?: PerformanceContext[];
}

export interface SongMetadataRequest {
  energy?: IntensityLevel;
  culturalOrigin?: string;
  style?: string;
  genre: Genre[];
  duration?: number;
  bpm?: number;
  key?: string;
  mood: Mood[];
  songName: string;
  artistName: string;
  albumName?: string;
  tempo: string;
  timeSignature?: string;
  releaseYear?: number;
  albumArtUrl?: string;
  algorhythmMetadata?: AlgorhythmMetadataRequest;
}

export interface StarMetadataRequest {
  energy?: IntensityLevel;
  culturalOrigin?: string;
  starName: string;
  realName?: string;
  gender: Gender;
  ageGroup: AgeGroup;
  ethnicity: string;
  bodyType?: string;
  height?: string;
  hairColor?: string;
  hairLength?: string;
  hairStyle?: string;
  eyeColor?: string;
  skinTone?: string;
  archetype?: string;
  mood: Mood[];
  musicalStyle?: string[];
  careerStage?: string;
  personalityTraits?: string[];
  performanceContext?: PerformanceContext[];
  makeupType?: string;
  colorPalette?: ColorPalette;
  hairTexture?: string;
  hairTreatment?: string;
  hairAccessories?: string;
  eyeMakeup?: string;
  lipMakeup?: string;
  faceMakeup?: string;
  makeupEffects?: string;
}

export interface LooksMetadataRequest {
  energy?: IntensityLevel;
  culturalOrigin?: string;
  outfitName: string;
  styleCategory: string;
  style: string;
  gender: Gender;
  occasion: string;
  colorScheme: string;
  primaryGarment: string;
  mood: Mood[];
  primaryColors: string[];
  patterns?: string[];
  targetAudience?: AgeGroup[];
  performanceContext?: PerformanceContext[];
  culturalContext?: CulturalContext[];
  materials?: string[];
  seasonality?: Seasonality;
  colorPalette?: ColorPalette;
  timeOfDay?: string;
  accessories?: string[];
  jewelryType?: string;
  brandNames?: string[];
  priceRange?: string;
  formality: string;
}

export interface MovesMetadataRequest {
  energy?: IntensityLevel;
  culturalOrigin?: string;
  tempo: string;
  moveName: string;
  danceStyle: string;
  difficulty: string;
  mood: Mood[];
  bodyParts?: string[];
  choreographerName?: string;
  substyle?: string;
  complexity?: string;
  intensity?: string;
  pace?: string;
  movementStyle?: string;
  spaceRequired?: string;
  groupSize?: string;
  skillLevelRequired?: string;
  costumeRequirements?: string;
  targetAudience?: AgeGroup[];
  performanceContext?: PerformanceContext[];
  audienceType?: string;
  bpmRangeMin?: number;
  bpmRangeMax?: number;
  floorWork?: boolean;
  partnered?: boolean;
  signatureMoves?: string[];
  techniquesRequired?: string[];
  propsNeeded?: string[];
}

export interface WorldsMetadataRequest {
  energy?: IntensityLevel;
  culturalOrigin?: string;
  style?: string;
  mood: Mood[];
  timeOfDay?: string;
  season?: Seasonality;
  versatility?: string;
  targetAudience?: AgeGroup[];
  performanceContext?: PerformanceContext[];
  culturalContext?: CulturalContext[];
  worldName: string;
  locationType: string;
  lighting?: string;
  dominantColors?: string[];
  atmosphere?: string;
  weather?: string;
  setting?: string;
  environmentType?: string;
  lightingStyle?: string;
  spatialSize?: string;
  settingCategory?: string;
  specificLocation?: string;
  spaceSize?: string;
  layout?: string;
  depth?: string;
  colorPalette?: ColorPalette;
  lightingDirection?: string;
  geographicRegion?: string;
  architecturalStyle?: string;
  backdropType?: string;
  productionValue?: string;
  technicalComplexity?: string;
  movementSpace?: string;
  audioAcoustics?: string;
  crowdCapacity?: string;
}

interface BaseAssetRequest {
  category: string;
  subcategory: string;
  description: string;
  creatorDescription?: string;
  tags: string[];
}

export interface SongAssetRequest extends BaseAssetRequest {
  metadata: SongMetadataRequest;
}

export interface StarAssetRequest extends BaseAssetRequest {
  aiDescription?: string;
  metadata: StarMetadataRequest;
}

export interface LookAssetRequest extends BaseAssetRequest {
  aiDescription?: string;
  metadata: LooksMetadataRequest;
}

export interface MoveAssetRequest extends BaseAssetRequest {
  aiDescription?: string;
  metadata: MovesMetadataRequest;
}

export interface WorldAssetRequest extends BaseAssetRequest {
  aiDescription?: string;
  metadata: WorldsMetadataRequest;
}

interface BaseAssetUpdateRequest<TMetadata> {
  description: string;
  tags: string[];
  metadata: TMetadata;
}

export type SongAssetUpdateRequest = BaseAssetUpdateRequest<SongMetadataRequest>;
export type StarAssetUpdateRequest = BaseAssetUpdateRequest<StarMetadataRequest>;
export type LookAssetUpdateRequest = BaseAssetUpdateRequest<LooksMetadataRequest>;
export type MoveAssetUpdateRequest = BaseAssetUpdateRequest<MovesMetadataRequest>;
export type WorldAssetUpdateRequest = BaseAssetUpdateRequest<WorldsMetadataRequest>;

export interface SongMetadataDetailsResponse {
  songName?: string;
  artistName?: string;
  albumName?: string;
  releaseYear?: number;
  duration?: number;
  genre?: Genre[];
  energy?: IntensityLevel;
  tempo?: string;
  thumbnailPath?: string;
  culturalOrigin?: string;
  variantName?: string;
  variantType?: string;
  creatorDescription?: string;
  style?: string;
  bpm?: number;
  key?: string;
  mood?: Mood[];
  timeSignature?: string;
  algorhythmMetadata?: AlgorhythmMetadataResponse;
  thumbnailUrl?: string;
}

export interface StarMetadataResponse {
  energy?: IntensityLevel;
  culturalOrigin?: string;
  variantName?: string;
  variantType?: string;
  creatorDescription?: string;
  starName?: string;
  realName?: string;
  gender?: Gender;
  ageGroup?: AgeGroup;
  ethnicity?: string;
  mood?: Mood[];
  bodyType?: string;
  height?: string;
  hairColor?: string;
  hairLength?: string;
  hairStyle?: string;
  eyeColor?: string;
  skinTone?: string;
  archetype?: string;
  musicalStyle?: string[];
  careerStage?: string;
  personalityTraits?: string[];
  performanceContext?: PerformanceContext[];
  makeupType?: string;
  colorPalette?: ColorPalette;
  hairTexture?: string;
  hairTreatment?: string;
  hairAccessories?: string;
  eyeMakeup?: string;
  lipMakeup?: string;
  faceMakeup?: string;
  makeupEffects?: string;
}

export interface LookMetadataResponse {
  energy?: IntensityLevel;
  culturalOrigin?: string;
  variantName?: string;
  variantType?: string;
  creatorDescription?: string;
  outfitName?: string;
  style?: string;
  gender?: Gender;
  styleCategory?: string;
  occasion?: string;
  colorScheme?: string;
  primaryGarment?: string;
  mood?: Mood[];
  primaryColors?: string[];
  patterns?: string[];
  targetAudience?: AgeGroup[];
  performanceContext?: PerformanceContext[];
  culturalContext?: CulturalContext[];
  materials?: string[];
  seasonality?: Seasonality;
  timeOfDay?: string;
  accessories?: string[];
  jewelryType?: string;
  brandNames?: string[];
  priceRange?: string;
  formality?: string;
}

export interface MoveMetadataResponse {
  energy?: IntensityLevel;
  culturalOrigin?: string;
  variantName?: string;
  variantType?: string;
  creatorDescription?: string;
  tempo?: string;
  moveName?: string;
  danceStyle?: string;
  difficulty?: string;
  mood?: Mood[];
  bodyParts?: string[];
  choreographerName?: string;
  substyle?: string;
  complexity?: string;
  intensity?: string;
  pace?: string;
  movementStyle?: string;
  spaceRequired?: string;
  groupSize?: string;
  skillLevelRequired?: string;
  costumeRequirements?: string;
  targetAudience?: AgeGroup[];
  performanceContext?: PerformanceContext[];
  audienceType?: string;
  bpmRangeMin?: number;
  bpmRangeMax?: number;
  floorWork?: boolean;
  partnered?: boolean;
  signatureMoves?: string[];
  techniquesRequired?: string[];
  propsNeeded?: string[];
}

export interface WorldMetadataResponse {
  energy?: IntensityLevel;
  culturalOrigin?: string;
  variantName?: string;
  variantType?: string;
  creatorDescription?: string;
  mood?: Mood[];
  timeOfDay?: string;
  season?: Seasonality;
  versatility?: string;
  targetAudience?: AgeGroup[];
  performanceContext?: PerformanceContext[];
  culturalContext?: CulturalContext[];
  style?: string;
  worldName?: string;
  locationType?: string;
  lighting?: string;
  dominantColors?: string[];
  atmosphere?: string;
  weather?: string;
  setting?: string;
  environmentType?: string;
  lightingStyle?: string;
  spatialSize?: string;
  settingCategory?: string;
  specificLocation?: string;
  spaceSize?: string;
  layout?: string;
  depth?: string;
  colorPalette?: ColorPalette;
  lightingDirection?: string;
  geographicRegion?: string;
  architecturalStyle?: string;
  backdropType?: string;
  productionValue?: string;
  technicalComplexity?: string;
  movementSpace?: string;
  audioAcoustics?: string;
  crowdCapacity?: string;
}
